import { useEffect, useRef } from "react";
import { Redirect, Route, Switch, useLocation, useRoute, useSearch } from "wouter";
import type { Client } from "@shared/schema";
import { screenAt, type InterviewPosition } from "@shared/interview/chapters";
import { InterviewShell } from "@/components/interview/InterviewShell";
import { NoneAnswerProvider } from "@/components/interview/useNoneAnswer";
import SettingsPage from "@/pages/SettingsPage";
import { PlanFrame, type PlanTab } from "./PlanFrame";
import { PlanSidebar, type SidebarTarget } from "./PlanSidebar";
import { PlanHome } from "./PlanHome";
import { WherePage } from "./WherePage";
import { ContactsPage } from "./ContactsPage";
import { useTaskCounter } from "./useTaskCounter";
import { HOME_BOARD, fill, finalTouch, handoverPage, positionOfKey, returnPath, returnPlace } from "./planBoard";
import { NO_EXECUTOR_YET, useExecutorFirstName } from "./useExecutorFirstName";

/**
 * PLAN-HOME-1 (s228) — THE PLAN, for a planner, behind the interview flag (Reade, ruling 11: on in staging, off in
 * production; `useInterviewGate` decides). One frame (board 12) around every screen:
 *
 *   /dashboard            Home — the sidebar and the home page.
 *   /interview            the interview in progress, framed (ruling B); the sidebar steps aside for the full width.
 *   /plan/screen/:key     one screen opened from the sidebar or the home, under "Return to …" (?from= names where).
 *   /plan/where           Where things are (s237, ACROSS-YOUR-PLAN-1 PR B); a screen opened from it returns there.
 *   /plan/contacts        Contacts (s237 PR C); a role's screen opened from it returns there.
 *   /settings             Settings, in board 12's groups (ruling E).
 *   /executor             the Executor tab — an empty placeholder (ruling 12) with the board's preview line.
 *
 * A planner mid-interview lands in the interview on sign-in, exactly as before; the Planner tab and Home take them to
 * the home, and "Continue where you left off" brings them back.
 */
export function PlanApp({
  clientData,
  position,
  plannerName,
  onSignOut,
}: {
  clientData: Client;
  /** The saved interview position while the interview is in progress; `null` once it is finished. */
  position: InterviewPosition | null;
  plannerName: string;
  onSignOut: () => void;
}) {
  return (
    // The "none" answers of whichever screen is open report here, so the Executor tab's counter prunes too.
    <NoneAnswerProvider>
      <PlanRoutes clientData={clientData} position={position} plannerName={plannerName} onSignOut={onSignOut} />
    </NoneAnswerProvider>
  );
}



function PlanRoutes({ clientData, position, plannerName, onSignOut }: {
  clientData: Client; position: InterviewPosition | null; plannerName: string; onSignOut: () => void;
}) {
  const [location, setLocation] = useLocation();
  const inProgress = !!position;
  const counter = useTaskCounter(clientData.id);

  // On arrival mid-interview, land in the interview — the behaviour before the frame existed.
  const landed = useRef(false);
  useEffect(() => {
    if (landed.current) return;
    landed.current = true;
    if (inProgress && (location === "/" || location === "/dashboard")) setLocation("/interview", { replace: true });
  }, [inProgress, location, setLocation]);

  const open = (key: string, from: string) => setLocation(`/plan/screen/${key}?from=${from}`);
  const here = location.startsWith("/settings") ? "settings" : location.startsWith("/interview") ? "interview" : location.startsWith("/plan/where") ? "where" : location.startsWith("/plan/contacts") ? "contacts" : "home";
  const go = (t: SidebarTarget) => (t.kind === "home" ? setLocation("/dashboard") : t.kind === "where" ? setLocation("/plan/where") : t.kind === "contacts" ? setLocation("/plan/contacts") : open(t.key, here));

  const tab: PlanTab = location.startsWith("/executor") ? "exec" : "plan";
  const [, screenParams] = useRoute("/plan/screen/:key");
  const current = location === "/dashboard" ? "home" : location.startsWith("/plan/where") ? "where" : location.startsWith("/plan/contacts") ? "contacts" : screenParams?.key ?? "";
  const withSidebar = location === "/dashboard" || location.startsWith("/settings") || location.startsWith("/plan/where") || location.startsWith("/plan/contacts");

  const onContinue = () => {
    if (inProgress) return setLocation("/interview");
    const saved = clientData.interviewPosition as InterviewPosition | null;
    const key = saved ? screenAt(saved)?.key : undefined;
    if (key) open(key, "home");
  };

  return (
    <PlanFrame
      planner={plannerName}
      tab={tab}
      onTab={(t) => setLocation(t === "exec" ? "/executor" : "/dashboard")}
      onSettings={() => setLocation("/settings")}
      settingsOn={location.startsWith("/settings")}
      onSignOut={onSignOut}
      counter={counter}
      sidebar={withSidebar ? <PlanSidebar current={current} onGo={go} /> : undefined}
    >
      <Switch>
        <Route path="/dashboard">
          <PlanHome clientId={clientData.id} counter={counter} position={position ?? (clientData.interviewPosition as InterviewPosition | null)}
            onContinue={onContinue} onOpen={(key) => open(key, "home")} />
        </Route>
        <Route path="/interview">
          {inProgress ? <InterviewShell framed clientData={clientData} position={position!} onHome={() => setLocation("/dashboard")} /> : <Redirect to="/dashboard" />}
        </Route>
        <Route path="/plan/where">
          <WherePage clientId={clientData.id} onOpen={(key) => open(key, "where")} />
        </Route>
        <Route path="/plan/contacts">
          <ContactsPage clientId={clientData.id} onOpen={(key) => open(key, "contacts")} />
        </Route>
        <Route path="/plan/screen/:key">
          {(params) => <PlanScreen clientData={clientData} screenKey={params.key} onReturn={(from) => setLocation(returnPath(from, inProgress))}
            onHome={() => setLocation("/dashboard")}
            onMove={(key, from) => setLocation(`/plan/screen/${key}?from=${from}`, { replace: true })} />}
        </Route>
        <Route path="/settings">
          <div className="px-4 py-2 sm:px-6"><SettingsPage clientData={clientData} grouped /></div>
        </Route>
        <Route path="/executor">
          <ExecutorPreview clientId={clientData.id} />
        </Route>
        <Route>
          <Redirect to="/dashboard" />
        </Route>
      </Switch>
    </PlanFrame>
  );
}

/** One screen, opened from the plan: the interview's own screen in a visit (never saving the position), or a Final
 *  touches step (today's placeholder). */
function PlanScreen({ clientData, screenKey, onReturn, onMove, onHome }: {
  clientData: Client; screenKey: string; onReturn: (from: string) => void; onMove: (key: string, from: string) => void; onHome: () => void;
}) {
  const from = new URLSearchParams(useSearch()).get("from") ?? "home";
  const label = returnPlace(from);
  const position = positionOfKey(screenKey);
  const executor = useExecutorFirstName(clientData.id) || NO_EXECUTOR_YET;
  if (position) {
    // s228 (item 6d): a pill, Back or Save and continue in a visit REPLACES the address with the screen it moved to, so
    // a refresh stays there. The saved interview position is still never touched.
    return <InterviewShell key={screenKey} framed clientData={clientData} position={position} onHome={onHome}
      visit={{ returnLabel: label, onReturn: () => onReturn(from), onMove: (p) => { const k = screenAt(p)?.key; if (k) onMove(k, from); } }} />;
  }
  // s233 (D-233.74): a hand-over's page (print your plan, the recovery code page, the family meeting) — a placeholder too.
  const touch = finalTouch(screenKey) ?? handoverPage(screenKey);
  if (!touch) return <Redirect to="/dashboard" />;
  return (
    <div className="mx-auto w-full max-w-[1600px] px-4 pb-16 pt-6 sm:px-8" data-testid="plan-touch">
      {/* s233 (D-233.51): "← Home" on Final touches too; a visit from the home already reads it, so never two. */}
      {label !== HOME_BOARD.labels.home && (
        <button type="button" onClick={onHome}
          className="mb-5 mr-4 inline-flex min-h-8 items-center gap-1.5 text-[13px] text-sage-800 hover:underline"
          data-testid="plan-touch-home">
          ← {HOME_BOARD.labels.home}
        </button>
      )}
      <button type="button" onClick={() => onReturn(from)}
        className="mb-5 inline-flex min-h-8 items-center gap-1.5 text-[13px] text-sage-800 hover:underline"
        data-testid="plan-touch-return">
        ← {label}
      </button>
      <h1 className="font-serif text-[30px] leading-tight text-warm-charcoal" data-testid="plan-touch-title">{fill(touch.title, { executor })}</h1>
      <p className="mt-3 text-[15px] text-warm-muted" data-testid="plan-touch-placeholder">{touch.why}</p>
    </div>
  );
}

/** The Executor tab: an empty placeholder (Reade, ruling 12) carrying the board's preview line. */
function ExecutorPreview({ clientId }: { clientId: string }) {
  const executor = useExecutorFirstName(clientId);
  return (
    <div className="w-full px-4 pt-7 sm:px-8" data-testid="plan-executor">
      <div className="flex w-full items-center gap-3 rounded-[12px] bg-warm-stone-light px-[18px] py-3.5 text-[14.5px] leading-normal text-sage-800">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="shrink-0" aria-hidden="true">
          <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z" /><circle cx="12" cy="12" r="3" />
        </svg>
        <span data-testid="plan-executor-preview">{fill(HOME_BOARD.previewText, { executor: executor || NO_EXECUTOR_YET })}</span>
      </div>
    </div>
  );
}
